// CompanyPicker — the company a «شركة» reservation is booked for: a search over
// the hotel's companies (GET /hotel/companies?q=). The chosen company is a chip
// with «تغيير»; its customer id rides on the create body as
// `company_customer_id` (erp-core applies the contract nights and records the
// company on the stay). A failed read says so and offers a retry.

import { useState } from 'react';
import { Search, X } from 'lucide-react';
import { Button, TextInput } from '@/components';
import { ApiError } from '@/lib/api';
import { useDebounced } from '@/features/booking/public';
import type { CompanyItem } from '../api';
import { useCompanies } from '../data/companies';

export interface CompanyPickerProps {
  value: CompanyItem | null;
  onChange: (c: CompanyItem | null) => void;
}

export function CompanyPicker({ value, onChange }: CompanyPickerProps) {
  const [query, setQuery] = useState('');
  const q = useDebounced(query.trim(), 250);
  const list = useCompanies(q, !value); // a chosen company needs no search

  if (value) {
    return (
      <div className="e-input nr-box" data-testid="res-company">
        <span className="nr-box-text">{value.name}</span>
        <button type="button" className="e-iconbtn nr-clear" aria-label="تغيير الشركة" onClick={() => onChange(null)}>
          <X aria-hidden="true" />
        </button>
      </div>
    );
  }
  const items = list.data?.items ?? [];
  return (
    <>
      <TextInput icon={Search} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="ابحث باسم الشركة" aria-label="ابحث عن الشركة باسمها" data-testid="res-company-search" />
      {list.isError && (
        <div className="nr-error" role="alert">
          {list.error instanceof ApiError && list.error.detail ? list.error.detail : 'لم تنجح المحاولة.'}{' '}
          <Button variant="line" size="sm" onClick={() => void list.refetch()}>
            أعد المحاولة
          </Button>
        </div>
      )}
      {!list.isError && list.isSuccess && items.length === 0 && <span className="nr-hint">{q ? `لا نتائج لـ«${q}»` : 'لا شركات بعد.'}</span>}
      {items.length > 0 && (
        <ul className="nr-results" aria-label="نتائج البحث عن الشركة">
          {items.slice(0, 6).map((c) => (
            <li key={c.customer_id}>
              <button type="button" className="nr-result" onClick={() => onChange(c)}>
                <span className="nr-result-name">{c.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
